
import { useCallback } from 'react';
import { App } from 'antd';
import { smartCopywritingAPI } from '../services';
import type { UsageScenario } from '../types';
import type { UseSmartCopywritingState } from './useSmartCopywritingState';

export const useScenarioLogic = (
    state: UseSmartCopywritingState,
    setState: React.Dispatch<React.SetStateAction<UseSmartCopywritingState>>,
    setLoading: (key: keyof UseSmartCopywritingState['loading'], value: boolean) => void
) => {
    const { message } = App.useApp();

    const recommendScenarios = useCallback(async () => {
        console.log('recommendScenarios: Starting, state.project =', state.project);

        if (!state.project) {
            message.warning('项目信息缺失，请刷新页面或返回第一步');
            return [];
        }

        const selectedAudiences = state.audiences.filter(a => a.selected);
        if (selectedAudiences.length === 0) {
            message.warning('请先选择目标人群');
            return [];
        }

        const productName = state.productName || state.project.product_name || '';

        if (!productName) {
            message.warning('产品名称缺失，请返回第一步填写');
            return [];
        }

        setState(prev => ({ ...prev, currentStep: 3, scenarios: [] }));
        setLoading('scenarios', true);

        return new Promise<UsageScenario[]>((resolve, reject) => {
            const accumulatedScenarios: UsageScenario[] = [];

            smartCopywritingAPI.recommendScenariosStream(
                state.project!.id,
                {
                    product_name: productName,
                    selected_audiences: selectedAudiences,
                },
                {
                    onScenario: (scenario) => {
                        console.log('Stream received scenario:', scenario.name);
                        const newScenario = { ...scenario, selected: false };
                        accumulatedScenarios.push(newScenario);

                        setState(prev => ({
                            ...prev,
                            scenarios: [...prev.scenarios, newScenario]
                        }));
                    },
                    onComplete: (count) => {
                        console.log(`Stream complete, count: ${count}`);
                        setLoading('scenarios', false);
                        message.success(`推荐完成，共 ${accumulatedScenarios.length} 个使用场景`);
                        resolve(accumulatedScenarios);
                    },
                    onError: (errMsg) => {
                        console.error('Stream error:', errMsg);
                        setLoading('scenarios', false);
                        message.error(`场景推荐失败: ${errMsg}`);
                        setState(prev => ({ ...prev, currentStep: 2 }));
                        reject(new Error(errMsg));
                    }
                }
            );
        });
    }, [state.project, state.productName, state.audiences, message, setLoading, setState]);

    const selectScenarios = useCallback((selectedIds: string[]) => {
        setState(prev => ({
            ...prev,
            scenarios: prev.scenarios.map(scn => ({
                ...scn,
                selected: selectedIds.includes(scn.id)
            }))
        }));
    }, [setState]);

    return { recommendScenarios, selectScenarios };
};
